import React from 'react';
import { JIRA_SOFTWARE } from '@jira/platform__application-editions/src/application.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { ExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import {
	ExperienceSuccess,
	useExperienceSuccess,
} from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useProjectAnalyticsAttributes } from '../get-project/index.tsx';

const VIEW_VERSION_DETAILS_PAGE_EXPERIENCE_NAME = 'viewVersionDetailsPage';

type VersionDetailsPageExperienceStartProps = {
	experienceId: string | null;
};

export const VersionDetailsPageExperienceStart = ({
	experienceId,
}: VersionDetailsPageExperienceStartProps): React.JSX.Element => {
	const appEditions = useAppEditions();
	return (
		<ExperienceStart
			experienceId={experienceId}
			experience={VIEW_VERSION_DETAILS_PAGE_EXPERIENCE_NAME}
			analyticsSource="versionDetails"
			edition={appEditions.software}
			application={JIRA_SOFTWARE}
		/>
	);
};

type Attributes = {
	readonly [key: string]: string | number | boolean | null;
};

type OnExperienceFail = (location: string, error: Error, additionalAttributes?: Attributes) => void;

/**
 * The function returned by this hook should be called if an "unexpected error"
 * occurs, e.g. a JavaScript exception bubbles out of the Version Details Page.
 */
export const useViewVersionDetailsPageExperienceFail = (): OnExperienceFail =>
	useExperienceFail({
		experience: VIEW_VERSION_DETAILS_PAGE_EXPERIENCE_NAME,
	});

type OnExperienceSuccess = (additionalAttributes?: Attributes) => void;

export const useViewVersionDetailsPageExperienceSuccess = (): OnExperienceSuccess =>
	useExperienceSuccess({
		experience: VIEW_VERSION_DETAILS_PAGE_EXPERIENCE_NAME,
	});

/**
 * This component should be rendered in "expected error" scenarios, e.g. the
 * user does not have the correct permissions or application license to view
 * the Version Details Page.
 */
type ExpectedErrorSuccessProps = {
	reason: string;
};

export const ViewVersionDetailsPageExpectedErrorExperienceSuccessTracker = ({
	reason,
}: ExpectedErrorSuccessProps): React.JSX.Element => (
	<ExperienceSuccess
		experience={VIEW_VERSION_DETAILS_PAGE_EXPERIENCE_NAME}
		attributes={{
			reason,
		}}
	/>
);

export const ViewVersionDetailsPageExperienceSuccessTracker = (): React.JSX.Element => {
	const projectInfo = useProjectAnalyticsAttributes();

	return (
		<ExperienceSuccess
			experience={VIEW_VERSION_DETAILS_PAGE_EXPERIENCE_NAME}
			attributes={{
				...projectInfo,
			}}
		/>
	);
};
